import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReviewBadge, { useReviewBadges } from '../../components/ReviewBadge';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ExternalLink, ArrowLeft, CheckCircle2, Quote, Package, ArrowRight, GitCompare, Pencil, Play, Check, Minus, Mic, Star } from 'lucide-react';
import { useCompare, MAX_COMPARE } from '../../hooks/useCompare';
import { TrustBadge } from '../../components/TrustBadge';
import { LogoOrMark } from '../../components/Lettermark';
import { recordRecentSupplier } from '../../lib/recentlyViewed';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../components/ui/tooltip';
import { ImageGallery, TestimonialCarousel } from '../../components/RichBlocks';
import { JsonLd, breadcrumbSchema, faqSchema, ORIGIN, setSocialPreview } from '../../lib/seo';
import { ShareButton } from '../../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { live: 'Live', onboarding: 'Coming soon', piloting: 'Piloting', in_development: 'In development', concept: 'Concept' };
const inputCls = 'w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
const toEmbed = (u) => {
  if (!u) return u;
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return u;
};

const ytThumb = (u) => {
  const m = (u || '').match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};
const embedAutoplay = (u) => {
  const e = toEmbed(u);
  if (!e) return e;
  return e + (e.includes('?') ? '&' : '?') + 'autoplay=1&rel=0';
};

const VideoThumb = ({ url, title, onPlay, testid }) => {
  const thumb = ytThumb(url);
  return (
    <button type="button" onClick={() => onPlay(url)} className="relative block w-full aspect-video rounded-lg overflow-hidden border border-[#E5E7EB] bg-gray-900 group text-left" data-testid={testid}>
      {thumb
        ? <img src={thumb} alt={title || 'video'} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
        : <span className="absolute inset-0 flex items-end p-3 bg-gradient-to-t from-black/60 to-transparent"><span className="text-white text-sm font-medium line-clamp-2">{title || 'Watch video'}</span></span>}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform"><Play className="w-5 h-5 text-[#00A79D] fill-[#00A79D] ml-0.5" /></span>
      </span>
    </button>
  );
};


const SuggestEditModal = ({ slug, onClose }) => {
  const [fields, setFields] = useState([]);
  const [vals, setVals] = useState({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/suppliers/${slug}/editable`).then((r) => {
      setFields(r.data.fields || []);
      setVals(Object.fromEntries((r.data.fields || []).map((f) => [f.key, f.current_value || ''])));
    }).catch(() => {});
  }, [slug]);

  const submit = async () => {
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/suppliers/${slug}/suggest`, {
        fields: vals, submitted_by_email: email.trim(), submitted_by_name: name.trim() || null, source: 'supplier',
      });
      toast.success('Thanks, your update has been sent for review.');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="suggest-edit-modal">
        <DialogHeader><DialogTitle>Suggest an update</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-500 -mt-2">Propose changes to this listing. Our team reviews every suggestion before it goes live.</p>
        <div className="space-y-3 mt-2">
          {fields.map((f) => (
            <div key={f.key} data-testid={`suggest-field-${f.key}`}>
              <label className="text-sm font-semibold text-[#1A3A5F]">{f.label}</label>
              {f.key === 'description'
                ? <textarea rows={4} value={vals[f.key] || ''} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className={`${inputCls} mt-1`} data-testid={`suggest-input-${f.key}`} />
                : <input value={vals[f.key] || ''} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className={`${inputCls} mt-1`} data-testid={`suggest-input-${f.key}`} />}
            </div>
          ))}
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="suggest-name" />
            <input type="email" placeholder="Your email *" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="suggest-email" />
          </div>
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors disabled:opacity-60" data-testid="suggest-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Send suggested update
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SupplierPage = () => {
  const { slug } = useParams();
  const reviewBadges = useReviewBadges();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { isSelected, toggle } = useCompare();
  const [showSuggest, setShowSuggest] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [podcastOpen, setPodcastOpen] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    setLoading(true);
    setSimilar([]);
    const urlDemo = new URLSearchParams(window.location.search).get('demo');
    const demoQ = (sessionStorage.getItem('ctc_demo') === '1' || urlDemo === '1' || urlDemo === 'true') ? '?demo=1' : '';
    axios.get(`${API}/public/suppliers/${slug}${demoQ}`)
      .then((r) => { setS(r.data); recordRecentSupplier({ slug: r.data.slug, name: r.data.name, logo: r.data.logo }); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    axios.get(`${API}/public/suppliers/${slug}/similar?limit=4`)
      .then((r) => setSimilar(r.data.items || []))
      .catch(() => setSimilar([]));
    axios.post(`${API}/public/suppliers/${slug}/view`).catch(() => {});
  }, [slug]);

  // Unique per-supplier <title> + meta description (avoids duplicate titles on /suppliers/:slug).
  useEffect(() => {
    if (!s || !s.name) return;
    document.title = `${s.name} - Care Technology Supplier | Care Tech Compare`;
    const desc = (s.tagline || s.description || `${s.name} on Care Tech Compare - compare their care technology products, features and reviews.`)
      .replace(/<[^>]*>/g, '').slice(0, 300);
    let m = document.head.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', desc);
    setSocialPreview({
      title: `${s.name} - Care Technology Supplier | Care Tech Compare`,
      description: desc,
      url: `${ORIGIN}/suppliers/${s.slug}`,
      image: `${ORIGIN}/api/public/share/supplier/image/${s.slug}.jpg`,
    });
  }, [s]);

  if (loading) return <div className="py-32 text-center text-gray-400"><Loader2 className="w-7 h-7 animate-spin inline" /></div>;
  if (notFound || !s) return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Supplier not found</h1>
      <Link to="/browse" className="text-[#00A79D] font-semibold mt-4 inline-block">← Back to directory</Link>
    </div>
  );

  const hasRich = s.premium && (s.description || (s.products || []).length || (s.testimonials || []).length || (s.videos || []).length || (s.screenshots || []).length || (s.faqs || []).length || (s.accordions || []).length);

  return (
    <div data-testid="public-supplier-page">
      <JsonLd id="supplier-breadcrumb" data={breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Browse', url: '/browse' },
        { name: s.name },
      ])} />
      {s.faqs && s.faqs.length > 0 && <JsonLd id="supplier-faq" data={faqSchema(s.faqs.map((f) => ({ q: f.question || f.q, a: f.answer || f.a })))} />}
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/browse" className="text-sm text-gray-500 hover:text-[#00A79D] inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-4 h-4" /> All suppliers</Link>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <LogoOrMark logo={s.logo} name={s.name} imgClassName="h-16 w-auto max-w-[200px] object-contain" markClassName="h-16 w-16 text-xl rounded-lg" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-3xl tracking-tight font-bold text-gray-900">{s.name}</h1>
                <ReviewBadge badge={reviewBadges.suppliers[s.slug]} testid="supplier-ctc-reviewed-badge" />
                {s.featured && <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2.5 py-1 rounded-full" data-testid="supplier-featured-badge"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" /> Featured</span>}
                {s.tier && s.tier !== 'dormant'
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#1A3A5F] px-2.5 py-1 rounded-full" data-testid="supplier-tier-badge">{s.tier_label}</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-1 rounded-full" data-testid="supplier-tier-badge">Dormant</span>}
                {s.confirmed && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip><TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00857D] bg-[#00A79D]/10 px-2.5 py-1 rounded-full cursor-help" data-testid="supplier-confirmed-badge"><ShieldCheck className="w-3.5 h-3.5" /> Confirmed supplier</span>
                    </TooltipTrigger>
                    <TooltipContent style={{ color: '#ffffff', backgroundColor: '#1A3A5F' }} className="max-w-xs leading-relaxed py-2 px-3 border-0" data-testid="supplier-confirmed-badge-tooltip">This supplier has verified their company details and product information with Care Tech Compare.</TooltipContent></Tooltip>
                  </TooltipProvider>
                )}
                {!s.supplier_reviewed && !s.product_reviewed && <TrustBadge trust={s.trust} showUnverified testid="supplier-trust-badge" />}
                {s.supplier_reviewed && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full cursor-help" data-testid="supplier-reviewed-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Independently Assured</span>
                      </TooltipTrigger>
                      <TooltipContent style={{ color: '#ffffff', backgroundColor: '#1A3A5F' }} className="max-w-xs leading-relaxed py-2 px-3 border-0" data-testid="supplier-reviewed-badge-tooltip">Independently checked and assured by Care City. Assurance is earned, never bought - it can't be purchased.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {s.product_reviewed && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full cursor-help" data-testid="product-reviewed-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Product Assured</span>
                      </TooltipTrigger>
                      <TooltipContent style={{ color: '#ffffff', backgroundColor: '#1A3A5F' }} className="max-w-xs leading-relaxed py-2 px-3 border-0" data-testid="product-reviewed-badge-tooltip">This supplier's product has been independently checked and assured by Care City. Assurance is earned, never bought.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {s.status && s.status !== 'live' && <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">{STATUS_LABEL[s.status] || s.status}</span>}
              </div>
              {/* Independence principle - money never buys trust (shown wherever tier/review badges appear) */}
              <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1" data-testid="independence-note"><ShieldCheck className="w-3 h-3" /> Paid tiers buy visibility, never trust. "Independently Assured" is checked by Care City and can never be bought.</p>
              {s.tagline && <p className="text-gray-600 mt-2 max-w-2xl">{s.tagline}</p>}
            </div>
            <div className="flex flex-col gap-2">
              {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="supplier-visit-site"><ExternalLink className="w-4 h-4" /> Visit website</a>}
              <Link to="/how-to-choose" className="inline-flex items-center justify-center gap-1.5 border border-[#E5E7EB] hover:border-[#00A79D] text-gray-800 font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="supplier-shortlist-cta">Add to my shortlist</Link>
              <button onClick={() => { if (!toggle(s.slug)) toast.error(`You can compare up to ${MAX_COMPARE} suppliers`); }}
                className={`inline-flex items-center justify-center gap-1.5 font-semibold px-5 py-2.5 rounded-md transition-colors border ${isSelected(s.slug) ? 'bg-[#00A79D]/10 border-[#00A79D] text-[#00A79D]' : 'border-[#E5E7EB] hover:border-[#00A79D] text-gray-800'}`}
                data-testid="supplier-compare-btn"><GitCompare className="w-4 h-4" /> {isSelected(s.slug) ? 'Added to compare' : 'Add to compare'}</button>
              <ShareButton shareUrl={`${API}/public/share/supplier/${s.slug}`} title={`${s.name} | Care Tech Compare`} testid="supplier-share-btn" />
              <button onClick={() => setShowSuggest(true)} className="inline-flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] transition-colors" data-testid="supplier-suggest-edit"><Pencil className="w-3.5 h-3.5" /> Suggest an update</button>
            </div>
          </div>
        </div>
      </div>
      {showSuggest && <SuggestEditModal slug={s.slug} onClose={() => setShowSuggest(false)} />}

      {/* Unclaimed listing disclaimer (Dormant = unclaimed) */}
      {s.tier === 'dormant' && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4" data-testid="unclaimed-disclaimer">
          <div className="bg-[#F4F7F9] border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-gray-700 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="dormant-upgrade-note">
            <span className="flex-1"><b className="text-gray-900">This is a free listing.</b> The supplier hasn't claimed it, so the details may be incomplete and CTC hasn't confirmed the company's current trading status. Is this your company? Claim it to appear in directory filters and add your website, logo and products.</span>
            <Link to={`/claim/${s.slug}`} className="inline-flex items-center justify-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold text-sm px-4 py-2 rounded-md transition-colors shrink-0" data-testid="dormant-upgrade-claim">Claim this listing <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {/* About */}
          <section>
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">About {s.name}</h2>
            {s.description ? (
              <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">{s.description}</div>
            ) : (
              <p className="text-gray-600">{s.tagline || `${s.name} is listed in the care tech compare directory.`}{!hasRich && s.premium ? ' Full listing content coming soon.' : ''}</p>
            )}
          </section>

          {/* Products */}
          {(s.products || []).length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2"><Package className="w-5 h-5 text-[#00A79D]" /> Products</h2>
              <div className="space-y-3">
                {s.products.map((p, i) => {
                  const inner = (
                    <>
                      {p.image && (
                        <div className="aspect-video rounded-md overflow-hidden border border-[#E5E7EB] mb-3 bg-[#F1F5F4]">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{p.name}</h3>
                          {p.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full shrink-0" data-testid={`supplier-product-featured-${i}`}><Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Featured</span>}
                        </div>
                        {p.slug ? <span className="text-[#00A79D] text-sm inline-flex items-center gap-1 shrink-0">View <ArrowRight className="w-3.5 h-3.5" /></span>
                          : p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[#00A79D] text-sm inline-flex items-center gap-1 shrink-0">Learn more <ArrowRight className="w-3.5 h-3.5" /></a>}
                      </div>
                      {p.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{p.description}</p>}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {p.assurance === 'independently_assured'
                          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`supplier-product-assurance-${i}`}><CheckCircle2 className="w-3 h-3" /> Independently Assured</span>
                          : <span className="text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full" data-testid={`supplier-product-assurance-${i}`}>Self-asserted</span>}
                        {(() => {
                          const R = { in_development: 'In Development', pilot: 'In Pilot', available: 'Live' };
                          const unknown = s.claimed === false;
                          if (!unknown && (!p.availability || p.availability === 'available')) return null;
                          const label = unknown ? 'Unknown' : R[p.availability];
                          const cls = unknown ? 'text-gray-500 bg-gray-50 border-gray-200'
                            : p.availability === 'in_development' ? 'text-amber-700 bg-amber-50 border-amber-200'
                            : 'text-teal-700 bg-teal-50 border-teal-200';
                          return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid={`supplier-product-readiness-${i}`}>{label}</span>;
                        })()}
                      </div>
                      {(p.categories || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.categories.map((c) => <span key={c} className="text-[11px] text-[#00A79D] bg-[#00A79D]/5 px-2 py-0.5 rounded">{c}</span>)}
                        </div>
                      )}
                      {(p.settings || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {p.settings.map((c) => <span key={c} className="text-[11px] text-[#1A3A5F] bg-[#1A3A5F]/5 px-2 py-0.5 rounded">{c}</span>)}
                        </div>
                      )}
                    </>
                  );
                  return p.slug ? (
                    <Link key={p.slug} to={`/products/${p.slug}`} className={`block border rounded-lg p-4 hover:shadow-sm transition-all ${p.featured ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/30 hover:border-amber-400' : 'border-[#E5E7EB] hover:border-[#00A79D]'}`} data-testid={`supplier-product-${p.slug}`}>{inner}</Link>
                  ) : (
                    <div key={i} className={`border rounded-lg p-4 ${p.featured ? 'border-amber-300 ring-1 ring-amber-200 bg-amber-50/30' : 'border-[#E5E7EB]'}`}>{inner}</div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Category characteristics (paid tiers only) */}
          {s.characteristics && Object.keys(s.characteristics).length > 0 && (
            <section data-testid="supplier-characteristics">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#00A79D]" /> Key features</h2>
              <div className="space-y-4">
                {Object.entries(s.characteristics).map(([cat, feats]) => (
                  <div key={cat} className="border border-[#E5E7EB] rounded-lg p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{cat}</div>
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {feats.map((f) => (
                        <span key={f.label} className={`inline-flex items-center gap-1.5 text-sm ${f.have ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {f.have ? <Check className="w-4 h-4" /> : <Minus className="w-4 h-4" />} {f.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Podcast */}
          {s.podcast && s.podcast.post_url && (
            <section data-testid="supplier-podcast">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2"><Mic className="w-5 h-5 text-[#00A79D]" /> Podcast</h2>
              <div className="border border-[#E5E7EB] rounded-xl p-5 bg-white">
                <h3 className="font-semibold text-gray-900">{s.podcast.title}</h3>
                {s.podcast.summary && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{s.podcast.summary}</p>}
                {s.podcast.spotify_embed && (
                  <iframe title={s.podcast.title} src={s.podcast.spotify_embed} width="100%" height="152" frameBorder="0" allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="rounded-xl mt-4" data-testid="supplier-podcast-embed" />
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button type="button" onClick={() => setPodcastOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#00A79D] hover:bg-[#00857D] text-white rounded-full px-4 py-2 transition-colors" data-testid="supplier-podcast-listen"><Play className="w-3.5 h-3.5 fill-white" /> Listen now</button>
                  {s.podcast.spotify_url && <a href={s.podcast.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1DB954] hover:bg-[#1aa34a] text-white rounded-full px-4 py-2 transition-colors">Spotify</a>}
                  {s.podcast.apple_url && <a href={s.podcast.apple_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1A3A5F] hover:bg-[#12293f] text-white rounded-full px-4 py-2 transition-colors">Apple Podcasts</a>}
                  <Link to={s.podcast.post_url} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#E5E7EB] hover:border-[#00A79D] text-gray-700 rounded-full px-4 py-2 transition-colors" data-testid="supplier-podcast-link">Full episode page <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
              </div>
            </section>
          )}


          {/* Video */}
          {s.video_url && (
            <section>
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">Watch</h2>
              <div className="max-w-xl">
                <VideoThumb url={s.video_url} title={`${s.name} video`} onPlay={setVideoUrl} testid="supplier-video-main" />
              </div>
            </section>
          )}

          {/* Videos */}
          {(s.videos || []).length > 0 && (
            <section data-testid="supplier-videos">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">Videos</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.videos.map((v, i) => v.url && (
                  <div key={i}>
                    <VideoThumb url={v.url} title={v.title || `${s.name} video ${i + 1}`} onPlay={setVideoUrl} testid={`supplier-video-${i}`} />
                    {v.title && <p className="text-sm text-gray-600 mt-1">{v.title}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Screenshots gallery */}
          {(s.screenshots || []).length > 0 && (
            <ImageGallery images={s.screenshots} title="Screenshots" testid="supplier-screenshots" />
          )}

          {/* Accordions */}
          {(s.accordions || []).length > 0 && (
            <section data-testid="supplier-accordions">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">More information</h2>
              <div className="space-y-2">
                {s.accordions.map((a, i) => (
                  <details key={i} className="group border border-[#E5E7EB] rounded-lg bg-white">
                    <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-gray-900 flex items-center justify-between">{a.heading}<span className="text-[#00A79D] text-lg group-open:rotate-45 transition-transform">+</span></summary>
                    <div className="px-4 pb-4 text-sm text-gray-600 whitespace-pre-wrap">{a.body}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* FAQs */}
          {(s.faqs || []).length > 0 && (
            <section data-testid="supplier-faqs">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">Frequently asked questions</h2>
              <div className="space-y-2">
                {s.faqs.map((f, i) => (
                  <details key={i} className="group border border-[#E5E7EB] rounded-lg bg-white">
                    <summary className="cursor-pointer list-none px-4 py-3 font-medium text-gray-900 flex items-center justify-between">{f.question}<span className="text-[#00A79D] text-lg group-open:rotate-45 transition-transform">+</span></summary>
                    <div className="px-4 pb-4 text-sm text-gray-600 whitespace-pre-wrap">{f.answer}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Testimonials */}
          {(s.testimonials || []).length > 0 && (
            <TestimonialCarousel items={s.testimonials} testid="supplier-testimonials" />
          )}

          {/* Knowledge Hub articles by this supplier */}
          {(s.articles || []).length > 0 && (
            <section data-testid="supplier-articles">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">From the Knowledge Hub</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.articles.map((a) => (
                  <Link key={a.slug} to={`/knowledge-hub/${a.slug}`} className="block border border-[#E5E7EB] rounded-lg overflow-hidden bg-white hover:border-[#00A79D] hover:shadow-sm transition-all" data-testid={`supplier-article-${a.slug}`}>
                    {a.image && <div className="aspect-video bg-[#F1F5F4]"><img src={a.image} alt={a.title} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} /></div>}
                    <div className="p-4">
                      {a.category && <span className="text-[11px] text-[#00A79D] font-semibold uppercase tracking-wide">{a.category}</span>}
                      <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2">{a.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">Categories</h3>
            <div className="flex flex-wrap gap-1.5">
              {(s.all_categories || []).length ? s.all_categories.map((c) => <span key={c} className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">{c}</span>) : <span className="text-sm text-gray-400">-</span>}
            </div>
            {(s.accreditations || []).length > 0 && (
              <>
                <h3 className="font-heading font-semibold text-gray-900 mt-5 mb-2">Accreditations</h3>
                <div className="flex flex-wrap gap-1.5">{s.accreditations.map((a) => <span key={a} className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">{a}</span>)}</div>
              </>
            )}
          </div>

          {!s.premium && (
            <div className="bg-[#00A79D]/5 border border-[#00A79D]/30 rounded-lg p-5">
              <h3 className="font-heading font-semibold text-gray-900">Is this your company?</h3>
              <p className="text-sm text-gray-600 mt-1">Claim this listing to add your full profile, products and media.</p>
              <Link to={`/claim/${s.slug}`} className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold text-sm mt-3" data-testid="supplier-claim-cta">Claim &amp; get Confirmed <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
        </aside>
      </div>

      {/* Similar suppliers */}
      {similar.length > 0 && (
        <div className="border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="similar-suppliers">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-5">Similar suppliers</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {similar.map((x) => (
                <Link key={x.slug} to={`/suppliers/${x.slug}`} className="bg-white border border-[#E5E7EB] rounded-lg p-4 hover:border-[#00A79D] hover:shadow-sm transition-all flex flex-col" data-testid={`similar-card-${x.slug}`}>
                  <LogoOrMark logo={x.logo} name={x.name} imgClassName="h-8 w-auto max-w-[120px] object-contain" markClassName="h-8 w-8 text-xs rounded-md" />
                  <h3 className="font-semibold text-gray-900 text-sm mt-3">{x.name}</h3>
                  {x.tagline && <p className="text-xs text-gray-500 mt-1 line-clamp-2 flex-1">{x.tagline}</p>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(x.categories || []).slice(0, 2).map((c) => <span key={c} className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{c}</span>)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video lightbox */}
      <Dialog open={!!videoUrl} onOpenChange={(o) => { if (!o) setVideoUrl(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="supplier-video-modal">
          {videoUrl && (
            <>
              <div className="aspect-video w-full">
                <iframe className="w-full h-full" src={embedAutoplay(videoUrl)} title="Supplier video" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
              </div>
              <a href={videoUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs text-gray-300 hover:text-white py-2.5 bg-black">Watch on YouTube <ExternalLink className="w-3.5 h-3.5" /></a>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Podcast listen modal */}
      <Dialog open={podcastOpen} onOpenChange={setPodcastOpen}>
        <DialogContent className="max-w-lg" data-testid="supplier-podcast-modal">
          {s.podcast && (
            <>
              <DialogHeader><DialogTitle className="text-base pr-6 flex items-center gap-2"><Mic className="w-4 h-4 text-[#00A79D]" /> {s.podcast.title}</DialogTitle></DialogHeader>
              {s.podcast.spotify_embed && (
                <iframe title={s.podcast.title} src={s.podcast.spotify_embed} width="100%" height="232" frameBorder="0" allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="rounded-xl" />
              )}
              <div className="flex flex-wrap gap-2 mt-1">
                {s.podcast.spotify_url && <a href={s.podcast.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1DB954] hover:bg-[#1aa34a] text-white rounded-full px-4 py-2 transition-colors">Listen on Spotify</a>}
                {s.podcast.apple_url && <a href={s.podcast.apple_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1A3A5F] hover:bg-[#12293f] text-white rounded-full px-4 py-2 transition-colors">Listen on Apple Podcasts</a>}
                <Link to={s.podcast.post_url} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#E5E7EB] hover:border-[#00A79D] text-gray-700 rounded-full px-4 py-2 transition-colors">Full episode page</Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPage;
